import {
  Badge,
  CountryCode,
  FlagComponent,
  Icons,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@commenda/kakashi';
import {
  cn,
  getCountryName,
  getEntityDisplayName,
  getPrintableCorporationType,
  TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT,
} from '@commenda/utils';
import { Handle, NodeProps, Position, useStore } from '@xyflow/react';
import { ComponentType, memo, useEffect, useRef, useState } from 'react';
import type { EntityNode as EntityNodeType } from '../../OrgDiagram/graph/types';
import { TAX_NODE_HEIGHT, TAX_NODE_WIDTH } from '../layout';
import { CLASS_TO_SHAPE, type ShapeKey } from '../tax-chart-shapes';
import { useTaxChartContext } from '../TaxChartContext';
import { TaxChartShapeSection } from './TaxChartShapeSection';

// Tokens inlined as hex so values survive consuming app's Tailwind content-scanner.
// STROKE = fg-quaternary (#8F8B85, primary.softPearl[550]) — one step lighter
// than fg-tertiary; sits between the muted border-primary and the icon-stroke
// fg-tertiary used elsewhere on the canvas.
const FILL_REST = '#FFFFFF';
const FILL_HOVER = '#F8F6F1';
const STROKE = '#8F8B85';
const STROKE_W = 1;

const OPEN_DELAY = 120;
const CLOSE_DELAY = 100;

// Touch devices fire mouseenter on tap; gate hover logic on hover-capable input.
const canHover = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

interface ShapeProps {
  fill: string;
}

// Shapes paint to fill the bbox passed via viewBox. `fill` is the only prop —
// memo'd so React Flow re-rendering nodes (pan/zoom) doesn't repaint SVG paths
// when the fill is unchanged.

const RectangleShape = memo(({ fill }: ShapeProps) => (
  <svg width={TAX_NODE_WIDTH} height={TAX_NODE_HEIGHT} viewBox={`0 0 ${TAX_NODE_WIDTH} ${TAX_NODE_HEIGHT}`}>
    <rect
      x={STROKE_W / 2}
      y={STROKE_W / 2}
      width={TAX_NODE_WIDTH - STROKE_W}
      height={TAX_NODE_HEIGHT - STROKE_W}
      rx={6}
      fill={fill}
      stroke={STROKE}
      strokeWidth={STROKE_W}
    />
  </svg>
));
RectangleShape.displayName = 'RectangleShape';

// Equilateral-ish, apex up. Inset by stroke so the line isn't clipped at the bbox edge.
const TriangleShape = memo(({ fill }: ShapeProps) => {
  const w = TAX_NODE_WIDTH;
  const h = TAX_NODE_HEIGHT;
  const inset = STROKE_W;
  const points = `${w / 2},${inset} ${w - inset},${h - inset} ${inset},${h - inset}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polygon points={points} fill={fill} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round" />
    </svg>
  );
});
TriangleShape.displayName = 'TriangleShape';

// Rect with an ellipse outline inscribed inside it — ellipse touches the rect
// at the midpoint of each side and does not overflow. Ellipse drawn after rect
// so its stroke sits on top of the rect's fill.
const DisregardedShape = memo(({ fill }: ShapeProps) => {
  const w = TAX_NODE_WIDTH;
  const h = TAX_NODE_HEIGHT;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <rect
        x={STROKE_W / 2}
        y={STROKE_W / 2}
        width={w - STROKE_W}
        height={h - STROKE_W}
        rx={6}
        fill={fill}
        stroke={STROKE}
        strokeWidth={STROKE_W}
      />
      <ellipse
        cx={w / 2}
        cy={h / 2}
        rx={w / 2 - STROKE_W}
        ry={h / 2 - STROKE_W}
        fill="none"
        stroke={STROKE}
        strokeWidth={STROKE_W}
      />
    </svg>
  );
});
DisregardedShape.displayName = 'DisregardedShape';

const OvalShape = memo(({ fill }: ShapeProps) => {
  const w = TAX_NODE_WIDTH;
  const h = TAX_NODE_HEIGHT;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <ellipse
        cx={w / 2}
        cy={h / 2}
        rx={w / 2 - STROKE_W}
        ry={h / 2 - STROKE_W}
        fill={fill}
        stroke={STROKE}
        strokeWidth={STROKE_W}
      />
    </svg>
  );
});
OvalShape.displayName = 'OvalShape';

// Vertices at each edge midpoint, inset by stroke so the line isn't clipped.
const RhombusShape = memo(({ fill }: ShapeProps) => {
  const w = TAX_NODE_WIDTH;
  const h = TAX_NODE_HEIGHT;
  const inset = STROKE_W;
  const points = `${w / 2},${inset} ${w - inset},${h / 2} ${w / 2},${h - inset} ${inset},${h / 2}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polygon points={points} fill={fill} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round" />
    </svg>
  );
});
RhombusShape.displayName = 'RhombusShape';

const SHAPE_COMPONENT: Record<ShapeKey, ComponentType<ShapeProps>> = {
  rectangle: RectangleShape,
  triangle: TriangleShape,
  disregarded: DisregardedShape,
  rhombus: RhombusShape,
  oval: OvalShape,
};

// Max lines that fit inside each shape's inscribed text area at 14px/18 line-height.
// Beyond this, names truncate with an ellipsis instead of bleeding past the shape
// edge. Tuned to the same paddings used in SHAPE_TEXT_CLASS below.
const SHAPE_LINE_CLAMP: Record<ShapeKey, number> = {
  rectangle: 3,
  // Triangle text lives in the bottom half (40px tall) where the base is widest.
  triangle: 2,
  disregarded: 3,
  // Diamond is widest at its vertical midline; keep the label to the fitting center rows.
  rhombus: 2,
  oval: 3,
};

// Per-shape text overlay positioning. Triangle is narrow at the apex so text
// hugs the bottom (wider) edge; rect/disregarded use the full inscribed area;
// oval is padded extra so text stays inside the inscribed square.
const SHAPE_TEXT_CLASS: Record<ShapeKey, string> = {
  rectangle: 'absolute inset-0 flex items-center justify-center px-lg text-center',
  // Triangle apex is 0-wide; mid-height it's ~80px wide. Anchor text at the
  // vertical midline (`top-1/2 bottom-0` + `items-start`) so a single line
  // sits at center and longer names wrap downward into the wider base.
  // px-5xl (40px each side) keeps text width at ~80px, fitting the midline.
  triangle: 'absolute inset-x-0 top-1/2 bottom-0 flex items-start justify-center px-5xl text-center',
  disregarded: 'absolute inset-0 flex items-center justify-center px-lg text-center',
  // Narrowest at top/bottom; pad both axes to keep text inscribed.
  rhombus: 'absolute inset-0 flex items-center justify-center px-2xl py-lg text-center',
  oval: 'absolute inset-0 flex items-center justify-center px-2xl py-lg text-center',
};

const TaxChartNode = memo(({ data, id }: Pick<NodeProps<EntityNodeType>, 'data' | 'id'>) => {
  // Fall back to the neutral oval if the class is missing/unknown at runtime (e.g. FE served ahead of the BE)
  // so a version skew degrades one node's shape rather than crashing the whole diagram.
  const shapeKey = CLASS_TO_SHAPE[data.taxChartClass] ?? 'oval';
  const Shape = SHAPE_COMPONENT[shapeKey];
  const taxChart = useTaxChartContext();
  const hasLegalName = !!data.legalName?.trim();
  const displayName = getEntityDisplayName(data);

  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);
  // Lifted so the Change-shape modal (rendered inside the popover) survives the popover closing on mouse-leave.
  const [modalOpen, setModalOpen] = useState(false);
  const isExternal = data.isNonControlledEntity;
  // Shape controls render only in the tax chart (context is null in the OrgDiagram reuse). Externals are
  // overridable too — they default to the oval server-side and the user can pick a different shape.
  const canOverride = !!taxChart?.canOverride;
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribe to canvas zoom so the popover closes when the user zooms — Radix
  // Popper repositions on resize/scroll only, and xyflow pans+zooms via CSS
  // transform which fires neither. Mirrors EntityNode's close-on-zoom effect.
  const zoom = useStore((s) => s.transform[2]);
  useEffect(() => {
    setOpen(false);
  }, [zoom]);

  const { keyPersons, bankAccounts, taxClassification } = data;

  const typeLabel =
    data.businessEntityType && data.incorporationCountry
      ? getPrintableCorporationType(data.businessEntityType, data.incorporationCountry)
      : null;

  const hasDetailContent = keyPersons.count > 0 || bankAccounts.count > 0;
  // canOverride keeps the popover reachable for controlled entities that have no other detail to show.
  const hasPopoverContent = hasDetailContent || !!taxClassification || !!typeLabel || canOverride;

  useEffect(() => {
    return () => {
      if (openTimer.current) clearTimeout(openTimer.current);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const scheduleOpen = () => {
    setHovered(true);
    if (!hasPopoverContent || !canHover()) return;
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    openTimer.current = setTimeout(() => setOpen(true), OPEN_DELAY);
  };

  const scheduleClose = () => {
    setHovered(false);
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  };

  const countryName = getCountryName(data.incorporationCountry) ?? '';
  const fill = hovered ? FILL_HOVER : FILL_REST;

  return (
    // open || modalOpen keeps the popover (and the modal it hosts) mounted while the Change-shape dialog is open.
    <Popover open={open || modalOpen} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          className="nodrag nopan relative group"
          style={{
            width: TAX_NODE_WIDTH,
            height: TAX_NODE_HEIGHT,
            // Externals hover-interact (popover + shape override) but aren't click-navigable —
            // there's no entity page behind them, so the cursor stays default.
            cursor: isExternal ? 'default' : undefined,
          }}
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
        >
          <Shape fill={fill} />
          {/* Sibling overlay (not nested in <Shape />) so legalName changes don't re-render the SVG. */}
          <div className={SHAPE_TEXT_CLASS[shapeKey]} style={{ pointerEvents: 'none' }}>
            <span
              className={
                hasLegalName
                  ? 'font-semibold text-text-primary'
                  : 'font-medium text-text-disabled'
              }
              style={{
                fontSize: 14,
                fontWeight: 600,
                lineHeight: '18px',
                // Wrap long names within the shape; break mid-word for very long
                // tokens (legal names with no spaces) so they never bleed out.
                overflowWrap: 'anywhere',
                // Truncate to the per-shape line cap with an ellipsis when the
                // name still doesn't fit after wrapping.
                display: '-webkit-box',
                WebkitLineClamp: SHAPE_LINE_CLAMP[shapeKey],
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {displayName}
            </span>
          </div>
          <Handle
            type="target"
            position={Position.Top}
            className="!bg-transparent !border-0 !w-px !h-px !min-w-0 !min-h-0"
          />
          <Handle
            type="source"
            position={Position.Bottom}
            className="!bg-transparent !border-0 !w-px !h-px !min-w-0 !min-h-0"
          />
        </div>
      </PopoverAnchor>

      <PopoverContent
        className={cn(
          // Mirrors OrgDiagram EntityNode popover (Figma 711:3019).
          'w-[320px] p-lg flex flex-col gap-lg origin-left',
          'data-[state=open]:[animation-duration:150ms]',
          'data-[state=open]:[animation-timing-function:cubic-bezier(0.215,0.61,0.355,1)]',
          'data-[state=closed]:[animation-duration:100ms]',
          'motion-reduce:animate-none motion-reduce:transition-none',
        )}
        side="right"
        sideOffset={8}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-lg">
          <span className="size-9 shrink-0">
            {data.incorporationCountry && (
              <FlagComponent
                country={data.incorporationCountry as CountryCode}
                countryName={countryName}
                _className="size-9"
              />
            )}
          </span>
          <div className="flex flex-col gap-xxs min-w-0">
            {/* Wraps rather than truncates: the shape clamps long names, so this is where they're read in full. */}
            <span
              className={
                hasLegalName
                  ? 'txt-small-bold text-text-primary break-words'
                  : 'txt-small-medium text-text-disabled break-words'
              }
            >
              {displayName}
            </span>
            <div className="flex items-center gap-sm flex-wrap">
              {typeLabel && (
                <Badge size="sm" variant="gray">
                  {typeLabel}
                </Badge>
              )}
              {taxClassification && (
                <>
                  {typeLabel && (
                    <span className="size-[4px] rounded-full bg-bg-tertiary shrink-0" />
                  )}
                  <Badge size="sm" variant="gray">
                    {TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT[taxClassification]}
                  </Badge>
                </>
              )}
            </div>
          </div>
        </div>

        {hasDetailContent && (
          <>
            <div className="h-px bg-border-primary w-full" />
            <div className="flex flex-col gap-md">
              {bankAccounts.count > 0 && (
                <div className="flex gap-md items-start">
                  <span className="shrink-0 text-fg-primary flex items-center h-[20px]">
                    <Icons.Bank className="size-4" />
                  </span>
                  <div className="flex flex-wrap gap-xxs items-center">
                    {bankAccounts.data.map((ba) => (
                      <span
                        key={ba.id}
                        className="font-semibold text-text-primary px-xs py-xxs rounded-full text-[12px] leading-[15px]"
                      >
                        {ba.name ?? ba.id}
                      </span>
                    ))}
                    {bankAccounts.count > bankAccounts.data.length && (
                      <span className="font-semibold text-text-tertiary px-xs py-xxs rounded-full text-[12px] leading-[15px]">
                        +{bankAccounts.count - bankAccounts.data.length} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {keyPersons.count > 0 && (
                <div className="flex gap-md items-start">
                  <span className="shrink-0 text-fg-primary flex items-center h-[20px]">
                    <Icons.Users01 className="size-4" />
                  </span>
                  <div className="flex flex-wrap gap-xxs items-center">
                    {keyPersons.data.map((kp) => (
                      <span
                        key={kp.directorId}
                        className="font-semibold text-text-primary px-xs py-xxs rounded-full text-[12px] leading-[15px]"
                      >
                        {kp.name}
                      </span>
                    ))}
                    {keyPersons.count > keyPersons.data.length && (
                      <span className="font-semibold text-text-tertiary px-xs py-xxs rounded-full text-[12px] leading-[15px]">
                        +{keyPersons.count - keyPersons.data.length} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {taxChart?.canOverride && (
          <TaxChartShapeSection
            entityId={Number(id)}
            companyId={taxChart.companyId}
            currentClass={data.taxChartClass}
            hasOverride={data.taxChartClassOverride !== null}
            modalOpen={modalOpen}
            onModalOpenChange={setModalOpen}
          />
        )}
      </PopoverContent>
    </Popover>
  );
});
TaxChartNode.displayName = 'TaxChartNode';

export { TaxChartNode };
